import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { EXCHANGE_AUTH_HANDOFF_CODE_MUTATION } from "@/graphql/auth";
import { apolloClient } from "@/lib/apollo/apollo-client";
import { type User, useAuthStore } from "@/lib/store/auth-enhanced";

type Status = "pending" | "success" | "error";

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [status, setStatus] = useState<Status>("pending");
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!code) {
      setStatus("error");
      setError("Missing handoff code");
      return;
    }

    (async () => {
      try {
        const { data } = await apolloClient.mutate({
          mutation: EXCHANGE_AUTH_HANDOFF_CODE_MUTATION,
          variables: { input: { code } },
        });

        if (!data?.exchangeAuthHandoffCode) {
          throw new Error("Empty handoff response");
        }

        const { accessToken, refreshToken, user } = data.exchangeAuthHandoffCode;
        useAuthStore
          .getState()
          .login({ accessToken, refreshToken: refreshToken || undefined }, user as User);
        setStatus("success");
      } catch (err) {
        setStatus("error");
        setError(err instanceof Error ? err.message : "Handoff failed");
      }
    })();
  }, [code]);

  if (status === "success") {
    return <Navigate to="/" replace />;
  }

  if (status === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-4 text-center">
          <h1 className="text-xl font-semibold">Sign-in link expired</h1>
          <p className="text-sm text-muted-foreground">{error ?? "Unable to sign you in."}</p>
          <Navigate to="/login" replace />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
        Signing you in…
      </div>
    </div>
  );
}
